import { useEffect, useRef } from "react";

export interface CurrentPositionOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  maxAgeMs?: number;
  maxAccuracyMeters?: number;
}

/** Wait for a fresh usable fix instead of accepting the first coarse network fix. */
export function acquireCurrentPosition(
  geo: Pick<Geolocation, "watchPosition" | "clearWatch">,
  { signal, timeoutMs = 20000, maxAgeMs = 10000, maxAccuracyMeters = 100 }: CurrentPositionOptions = {},
): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    let watchId: number | undefined;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const clear = () => {
      if (timer !== undefined) clearTimeout(timer);
      if (watchId !== undefined) {
        try { geo.clearWatch(watchId); } catch { /* A disconnected native page may no longer accept messages. */ }
      }
      signal?.removeEventListener("abort", abort);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      clear();
      reject(error);
    };
    const abort = () => fail(new DOMException("Location request cancelled", "AbortError"));
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener("abort", abort, { once: true });
    timer = setTimeout(() => fail({ code: 3, message: "לא התקבל מיקום מדויק בזמן. בדקו ששירותי המיקום פעילים ונסו שוב." }), timeoutMs);
    try {
      watchId = geo.watchPosition(position => {
        if (settled) return;
        const { latitude, longitude, accuracy } = position.coords;
        const age = Date.now() - position.timestamp;
        if (!Number.isFinite(latitude) || Math.abs(latitude) > 90 ||
            !Number.isFinite(longitude) || Math.abs(longitude) > 180 ||
            !Number.isFinite(accuracy) || accuracy < 0 || accuracy > maxAccuracyMeters ||
            !Number.isFinite(age) || age > maxAgeMs || age < -5000) return;
        settled = true;
        clear();
        resolve(position);
      }, error => {
        // A lost signal may recover within this bounded acquisition. A denied
        // permission cannot; stop immediately without creating a retry loop.
        if (error.code === 1) fail(error);
      }, { enableHighAccuracy: true, maximumAge: Math.min(maxAgeMs, 5000), timeout: timeoutMs });
      // Some bridges and test doubles invoke callbacks synchronously.
      if (settled) {
        try { geo.clearWatch(watchId); } catch { /* The result is already settled. */ }
      }
    } catch (error) { fail(error); }
  });
}

interface CurrentLocationOptions {
  enabled: boolean;
  onPosition: (position: GeolocationPosition) => void;
  onError?: (error: unknown) => void;
}

/** Acquire once on foreground/permission grant, without running a permanent idle GPS watch. */
export function useCurrentLocation({ enabled, onPosition, onError }: CurrentLocationOptions) {
  const callbacks = useRef({ onPosition, onError });
  callbacks.current = { onPosition, onError };
  const denied = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let active: AbortController | null = null;
    let status: PermissionStatus | null = null;
    const nativeGeo = () => (window as Window & { DerechLocation?: Geolocation }).DerechLocation;
    const start = () => {
      if (!alive || active || denied.current || document.hidden) return;
      // The browser must already have permission. The native bridge serializes
      // requests with its own startup OS permission flow; WebView's Permissions
      // API may continue reporting "prompt" even after the OS grant.
      const native = nativeGeo();
      if (!native && status?.state !== "granted") return;
      if (native && document.readyState !== "complete") return;
      const geo = native ?? navigator.geolocation;
      if (!geo) return;
      const controller = new AbortController();
      active = controller;
      void acquireCurrentPosition(geo, { signal: controller.signal }).then(position => {
        if (alive && !controller.signal.aborted) callbacks.current.onPosition(position);
      }, error => {
        if (!alive || controller.signal.aborted) return;
        if ((error as { code?: number })?.code === 1) denied.current = true;
        callbacks.current.onError?.(error);
      }).finally(() => { if (active === controller) active = null; });
    };
    const stop = () => { active?.abort(); active = null; };
    const visibility = () => { if (document.hidden) stop(); else start(); };
    const permissionChanged = () => {
      if (status?.state === "granted") { denied.current = false; start(); }
      else if (status?.state === "denied") { denied.current = true; stop(); }
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("load", start);
    // Existing Android/iOS bridges emit this once DerechLocation is available.
    window.addEventListener("derech:car-ready", start);
    start();
    try {
      void navigator.permissions?.query({ name: "geolocation" }).then(permission => {
        if (!alive) return;
        status = permission;
        permission.addEventListener("change", permissionChanged);
        // Native acquisition already started (or is waiting for load). A late
        // WebView permission query must not start a second completed request.
        if (!nativeGeo() || denied.current && permission.state === "granted") permissionChanged();
      }).catch(() => { /* Safari may not expose a geolocation permission query. */ });
    } catch { /* Querying permissions is optional; explicit locate still works. */ }
    return () => {
      alive = false;
      stop();
      status?.removeEventListener("change", permissionChanged);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("load", start);
      window.removeEventListener("derech:car-ready", start);
    };
  }, [enabled]);
}
